/**
 * OnboardingIntro.tsx
 * Welcome screen for the onboarding flow.
 * Displays introductory message and 4 key benefits with icons.
 * Mode-aware: shows companion-specific or ILE-specific copy based on isCompanionMode.
 * Users can start the onboarding or skip for later.
 */
import React from "react";
import { View, Platform } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import OnboardingBenefitCard from "./common/OnboardingBenefitCard";
import { CompanionVideoPlayer } from "../shared/CompanionVideoPlayer";
import { colors, spacing, spacingStyles, textColorStyles } from "../../../styles/design-system";
import { LockIcon, BookOpenIcon, ChatsIcon, LightbulbIcon, StarIcon, ClockIcon } from "@phosphor-icons/react";

export interface OnboardingIntroProps {
  onStart: () => void; // Callback to proceed to onboarding steps
  onSkip: () => void; // Callback to dismiss onboarding
  isCompanionMode?: boolean; // Controls which copy to display
}

function OnboardingIntro({ onStart, onSkip, isCompanionMode = true }: OnboardingIntroProps) {
  const titleKey = isCompanionMode ? "companion_onboarding_intro_title" : "ile_onboarding_intro_title";
  const subtitleKey = isCompanionMode ? "companion_onboarding_intro_subtitle" : "ile_onboarding_intro_subtitle";

  const companionBenefits = [
    {
      icon: <LockIcon size={20} weight="regular" color={colors.success[600]} />,
      titleKey: "companion_onboarding_intro_benefit_1",
      subtitleKey: "companion_onboarding_intro_benefit_1_subtitle",
      iconBackgroundColor: colors.success[100],
    },
    {
      icon: <BookOpenIcon size={20} weight="regular" color={colors.blue[600]} />,
      titleKey: "companion_onboarding_intro_benefit_2",
      subtitleKey: "companion_onboarding_intro_benefit_2_subtitle",
      iconBackgroundColor: colors.blue[100],
    },
    {
      icon: <ChatsIcon size={20} weight="regular" color={colors.purple[600]} />,
      titleKey: "companion_onboarding_intro_benefit_3",
      subtitleKey: "companion_onboarding_intro_benefit_3_subtitle",
      iconBackgroundColor: colors.purple[100],
    },
    {
      icon: <LightbulbIcon size={20} weight="regular" color={colors.warning[500]} />,
      titleKey: "companion_onboarding_intro_benefit_4",
      subtitleKey: "companion_onboarding_intro_benefit_4_subtitle",
      iconBackgroundColor: colors.warning[100],
    },
  ];

  const ileBenefits = [
    {
      icon: <BookOpenIcon size={20} weight="regular" color={colors.blue[600]} />,
      titleKey: "ile_onboarding_intro_benefit_1",
      subtitleKey: "ile_onboarding_intro_benefit_1_subtitle",
      iconBackgroundColor: colors.blue[100],
    },
    {
      icon: <ClockIcon size={20} weight="regular" color={colors.success[600]} />,
      titleKey: "ile_onboarding_intro_benefit_2",
      subtitleKey: "ile_onboarding_intro_benefit_2_subtitle",
      iconBackgroundColor: colors.success[100],
    },
    {
      icon: <ChatsIcon size={20} weight="regular" color={colors.purple[600]} />,
      titleKey: "ile_onboarding_intro_benefit_3",
      subtitleKey: "ile_onboarding_intro_benefit_3_subtitle",
      iconBackgroundColor: colors.purple[100],
    },
    {
      icon: <StarIcon size={20} weight="regular" color={colors.warning[500]} />,
      titleKey: "ile_onboarding_intro_benefit_4",
      subtitleKey: "ile_onboarding_intro_benefit_4_subtitle",
      iconBackgroundColor: colors.warning[100],
    },
  ];

  const benefits = isCompanionMode ? companionBenefits : ileBenefits;

  return (
    <View style={[spacingStyles.mNone, { alignItems: "center" }]}>
      <CustomText text={titleKey} size="xxl" weight="bold" style={{ textAlign: "center", marginBottom: spacing.xs }} />
      <CustomText
        text={subtitleKey}
        size="m"
        style={[textColorStyles.textLight, { textAlign: "center", marginBottom: spacing.xl }]}
      />

      {isCompanionMode && Platform.OS === "web" && (
        <CompanionVideoPlayer style={{ width: "100%", marginBottom: spacing.xl }} />
      )}

      <View style={{ width: "100%", gap: spacing.md, marginBottom: spacing.xl }}>
        {benefits.map((benefit) => (
          <OnboardingBenefitCard
            key={benefit.titleKey}
            icon={benefit.icon}
            titleKey={benefit.titleKey}
            subtitleKey={benefit.subtitleKey}
            iconBackgroundColor={benefit.iconBackgroundColor}
          />
        ))}
      </View>

      <CustomButton
        text="buttons.companion_onboarding_intro_start"
        onPress={onStart}
        styleType="primary"
        style={{ width: "100%", marginBottom: spacing.md }}
      />
      <CustomButton
        text="buttons.companion_onboarding_intro_skip"
        onPress={onSkip}
        styleType="primaryTransparent"
        style={{ width: "100%" }}
        textStyle={{ color: colors.black }}
      />
    </View>
  );
}

export default OnboardingIntro;
